import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import {
  DocumentFormGroup,
  DescriptionFormGroup,
  DeadlineFormGroup,
  ContractFormGroup,
  EstimateFormGroup,
} from "./FormGroups";
import { defaultValues } from "./defaultValues";
import { createFormSchema } from "./formSchema";
import { useCreateTenderMutationHook } from "@/hooks/mutations";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { convertBpToDecimal, convertDecimalToBp } from "@/utils/helpers";
import { TenderType, GetAiImportByIdQuery, GetAiImportByIdQueryVariables, GetAiImportByIdDocument, Document } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import React, { Dispatch, SetStateAction, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { merge, debounce } from "lodash";
import CreateTenderSideNavMenu from "../Shared/CreateTenderSideNavMenu";
import { CreateTenderFormGroups } from "@/constants/enums";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { HamburgerMenuIcon } from "@radix-ui/react-icons";
import { useBreakpoint } from "@/hooks";
import { FormGroupHeader } from "@/components/shared";
import { ExecutiveSummaryFormGroup } from "../Shared/FormGroups/ExecutiveSummaryFormGroup";
import { Button } from "@/components/ui/button";
import { FormGroupItem } from "../Shared/types";
import { formGroupSchemas } from "./formSchema";
import { processDocuments } from "../Shared/helpers";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { SidebarComponent } from "./Sidebar";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useFormGroupScrollSpy } from "../Shared/helpers";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import { populateFormWithMockData } from "./mocks";

/**
 * Function to recursively filter out null values from an object
 * The function will filter out null values and __typename fields
 * @param obj json object
 * @returns json object without null values
 */
function filterNullValues(obj) {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([key, value]) => value !== null && key !== "__typename")
      .map(([key, value]) =>
        [key, value && typeof value === "object" && !(value instanceof Date) ? filterNullValues(value) : value]
      )
  );
}

const dateFields = ["publication", "submission", "questions", "siteVisit", "outsideInformation", "startDate", "endDate"];

/**
 * Function to recursively loop through the values from an object
 * and check if it is a date field and convert the string into a date object
 * and return the object with the converted date fields
 * @param obj json object
 * @returns json object with date fields converted to date objects
 */
function convertDateFields(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      dateFields.includes(key) && typeof value === "string"
        ? new Date(value)
        : value && typeof value === "object" && !(value instanceof Date)
          ? convertDateFields(value)
          : value,
    ])
  );
}

/**
 * Function to prefill the form with AI data
 * @param form react hook form object
 * @param aiData AI data object to prefill the form
 * @param setFormKey react hook state to remount the form
 * @param setPrefilled react hook state to set the form as prefilled
 */
function prefillFormWithAiData(
  form: any,
  aiData: any,
  executiveSummary: string,
  setFormKey: Dispatch<SetStateAction<string>>,
  setPrefilled: Dispatch<SetStateAction<boolean>>
) {
  const checkExist = (value: any) => value != null;

  // special cases that need to be converted to string to render in input correctly
  const ownerId = checkExist(aiData.description?.ownerId) ? aiData.description.ownerId.toString() : defaultValues.description.ownerId;

  // convert basis point to decimal for specific fields
  const margin = checkExist(aiData.estimate?.margin) ? convertBpToDecimal(aiData.estimate.margin) : defaultValues.estimate.margin;
  const escalationClause = checkExist(aiData.contract?.escalationClause) ? convertBpToDecimal(aiData.contract.escalationClause) : defaultValues.contract.escalationClause;
  const priceCriteria = checkExist(aiData.contract?.priceCriteria) ? convertBpToDecimal(aiData.contract.priceCriteria) : defaultValues.contract.priceCriteria;
  const qualityCriteria = checkExist(aiData.contract?.qualityCriteria) ? convertBpToDecimal(aiData.contract.qualityCriteria) : defaultValues.contract.qualityCriteria;
  const referenceCriteria = checkExist(aiData.contract?.referenceCriteria) ? convertBpToDecimal(aiData.contract.referenceCriteria) : defaultValues.contract.referenceCriteria;

  // Process AI data by filtering nulls, converting dates, and merging with defaults
  const modifiedAiData = merge(
    {},
    defaultValues,
    convertDateFields(filterNullValues(aiData))
  );

  // Overwrite specific fields with special cases
  modifiedAiData.description.ownerId = ownerId;
  modifiedAiData.estimate.margin = margin;
  modifiedAiData.contract.escalationClause = escalationClause;
  modifiedAiData.contract.priceCriteria = priceCriteria;
  modifiedAiData.contract.qualityCriteria = qualityCriteria;
  modifiedAiData.contract.referenceCriteria = referenceCriteria;
  modifiedAiData.executiveSummary = executiveSummary;

  // Prefill the form with the AI data
  form.reset(modifiedAiData);

  setPrefilled(true);

  // remount the form to visually set certain inputs as filled, issue with shadcn
  setFormKey("ai-form-filled");
};


function FormComponent() {

  // States
  const [formKey, setFormKey] = useState("ai-form-blank");
  const [prefilled, setPrefilled] = useState(false);
  const [activeFormGroup, setActiveFormGroup] = useState<CreateTenderFormGroups>(CreateTenderFormGroups.DOCUMENTS);
  const [isOpen, setIsOpen] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(100);

  // Check if we are in dev mode
  const isDevMode = import.meta.env.VITE_APP_ENV === "dev";

  // Create refs for each form group card
  const formGroupCardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // debounce setting of setLeftPanelWidth
  const debouncedSetWidth = useCallback(
    debounce((panelSize: { asPercentage: number; inPixels: number }) => {
      setLeftPanelWidth(panelSize.asPercentage);
    }, 50), // 50ms debounce time
    []
  );

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const isMdBreakpoint = useBreakpoint("md");
  const { t } = useTranslation(["translation", "schemas"]);
  const [searchParams] = useSearchParams();
  const aiImportID = searchParams.get("importID");

  useEffect(() => {
    if (aiImportID) {
      setActiveFormGroup(CreateTenderFormGroups.DESCRIPTION);
    }
    else {
      setActiveFormGroup(CreateTenderFormGroups.DOCUMENTS);
    }
  }, []);

  const FormSchema = useMemo(() => createFormSchema(t, aiImportID), [t, aiImportID]);

  const { data: aiImportByIdData, loading: aiImportByIdLoading } = useQuery<GetAiImportByIdQuery, GetAiImportByIdQueryVariables>(GetAiImportByIdDocument, {
    variables: {
      getAiImportByIdId: Number(aiImportID)
    },
    fetchPolicy: "cache-and-network",
    skip: !aiImportID
  });
  const { createTenderMutation, loading } = useCreateTenderMutationHook();

  const form = useForm<z.infer<typeof FormSchema>>({
    shouldUnregister: false, // Ensure that the form values are not unregistered while navigating between form groups
    resolver: zodResolver(FormSchema) as Resolver<z.infer<typeof FormSchema>>,
    defaultValues,
    mode: "onChange",
    shouldFocusError: false
  });

  const onInvalid = (errors: unknown) => {
    console.error(errors);
  };

  // Contains the form groups to render and what to validation to trigger on navigating with next button
  const formGroups: FormGroupItem[] = [];

  // Conditionally add the DocumentFormGroup
  if (!aiImportID) {
    formGroups.push({
      group: CreateTenderFormGroups.DOCUMENTS,
      components: [<DocumentFormGroup key="document" form={form} />],
      keys: ["document"],
      description: t("newtender.formgroups.document.description"),
      label: t("newtender.navlinks.document"),
    });
  }

  // Add the rest of the form groups
  formGroups.push(
    {
      group: CreateTenderFormGroups.DESCRIPTION,
      components: [
        <DescriptionFormGroup
          key="description"
          form={form}
          prefilled={prefilled}
          leftPanelWidth={leftPanelWidth}
        />,
      ],
      keys: ["description", "operationalOrganizationIds"],
      description: t("newtender.formgroups.description.description"),
      label: t("newtender.navlinks.description"),
      additionalFormGroup: [CreateTenderFormGroups.OPERATIONALORGANIZATION],
    },
    {
      group: CreateTenderFormGroups.DEADLINES,
      components: [<DeadlineFormGroup key="deadline" form={form} leftPanelWidth={leftPanelWidth} />],
      keys: ["deadline"],
      description: t("newtender.formgroups.deadline.description"),
      label: t("newtender.navlinks.deadlines"),
    },
    {
      group: CreateTenderFormGroups.CONTRACT,
      components: [<ContractFormGroup key="contract" form={form} leftPanelWidth={leftPanelWidth} />],
      keys: ["contract"],
      description: t("newtender.formgroups.contract.description"),
      label: t("newtender.navlinks.contract"),
    },
    {
      group: CreateTenderFormGroups.ESTIMATE,
      components: [<EstimateFormGroup key="estimate" form={form} leftPanelWidth={leftPanelWidth} />],
      keys: ["estimate"],
      description: t("newtender.formgroups.estimate.description"),
      label: t("newtender.navlinks.estimate"),
    }
  );

  if (!aiImportID) {
    formGroups.push({
      group: CreateTenderFormGroups.EXECUTIVESUMMARY,
      components: [<ExecutiveSummaryFormGroup key="executiveSummary" form={form} />],
      keys: ["executiveSummary"],
      description: t("newtender.formgroups.executivesummary.description"),
      label: t("newtender.navlinks.executivesummary"),
    });
  }

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const { contract, estimate, executiveSummary, ...rest } = data;
    const { escalationClause, ...restContract } = contract;
    const { margin, ...restEstimate } = estimate;

    // Convert the form data to the correct format for the mutation
    const variables = {
      input: {
        ...rest,
        estimate: {
          ...restEstimate,
          margin: convertDecimalToBp(margin),
        },
        contract: {
          ...restContract,
          escalationClause: contract.escalationClause ? convertDecimalToBp(escalationClause as number) : undefined,
          priceCriteria: convertDecimalToBp(contract.priceCriteria as number),
          qualityCriteria: convertDecimalToBp(contract.qualityCriteria as number),
          referenceCriteria: convertDecimalToBp(contract.referenceCriteria as number),
        },
      },
      executiveSummary
    };

    // If tender type is private, remove fields that are not needed
    if (data.description.type === TenderType.Private) {
      delete variables.input.description.platform;
      delete variables.input.description.awardingAuthority;
      delete variables.input.deadline.questions;
      delete variables.input.deadline.siteVisit;
      delete variables.input.deadline.publication;
    }

    // Construct the variables for the mutation based on the presence of aiImportID
    if (aiImportID) {
      variables["aiImportId"] = Number(aiImportID);
    } else {
      if (!("document" in data)) {
        throw new Error("Document should exist in data when aiImportID is not present");
      }

      const { document } = data;
      const { documents, categoryIndexes } = processDocuments(
        document as Record<string, File[]>,
        {
          publication: "publications",
          serviceDescription: "serviceDescription",
          contract: "contracts",
          otherDocuments: "otherDocuments",
          priceForms: "priceForms"
        }
      );

      // Remove document field from variables as documents will be passed in as separate fields
      delete variables["input"]["document"];

      // Add processed documents
      variables["documents"] = documents;

      // Add category indexes
      Object.entries(categoryIndexes).forEach(([key, value]) => {
        variables[key] = value;
      });
    }

    createTenderMutation({ variables }).then((results) => {
      // navigate to the tender page using result id
      const tenderId = results.data?.createTender.id;
      if (tenderId) {
        form.reset(defaultValues);
        navigate(`/tender/${tenderId}`);
      }
    });
  }

  const handleSubmitOnClick = form.handleSubmit(onSubmit, onInvalid);

  // FIXME: #226 - https://github.com/Santa-Technologies/Frontend/issues/226
  useEffect(() => {
    form.reset(defaultValues);
    setFormKey("ai-form-reset");
  }, [location.pathname]);

  useEffect(() => {
    if (aiImportByIdData && !aiImportByIdLoading) {
      const extractedData = aiImportByIdData.getAIImportById.structuredData;
      const extractedExecutiveSummary = aiImportByIdData.getAIImportById.executiveSummary;

      prefillFormWithAiData(form, extractedData, extractedExecutiveSummary, setFormKey, setPrefilled);
    }
  }, [aiImportByIdData, aiImportByIdLoading]);

  const submitButton = () => {
    return (
      <Button
        disabled={loading}
        onClick={handleSubmitOnClick}
        className="gap-2"
      >
        <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
        {t("newtender.btncreatetender")}
      </Button>
    );
  };

  const mockDataButton = () => {
    return (
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => populateFormWithMockData(form, "public")}
          className="gap-2"
          type="button"
        >
          🧪 Public Mock
        </Button>
        <Button
          variant="outline"
          onClick={() => populateFormWithMockData(form, "private")}
          className="gap-2"
          type="button"
        >
          🧪 Private Mock
        </Button>
      </div>
    );
  };

  /**
   * Custom useEffect hook.
   * Handles the observer for the active form group.
   */
  useFormGroupScrollSpy(
    formGroups,
    formGroupCardRefs,
    setActiveFormGroup,
    300
  );

  return (
    <div>
      <ResizablePanelGroup orientation="horizontal">
        <ResizablePanel onResize={debouncedSetWidth} defaultSize={50} minSize={20}>
          <div className="flex w-full">
            {/* Navigation Menu */}
            <div className="z-50">
              {
                isMdBreakpoint ? (
                  <CreateTenderSideNavMenu
                    formGroups={formGroups}
                    activeFormGroup={activeFormGroup}
                    form={form}
                    aiPrefilled={aiImportID ? true : false}
                    formGroupSchemas={formGroupSchemas}
                  />
                ) :
                  (
                    <div className="py-3 pl-4 pr-2">
                      <Sheet open={isOpen} onOpenChange={setIsOpen}>
                        <SheetTrigger data-cy="mobile-menu-trigger"><HamburgerMenuIcon className="icon-sm" /></SheetTrigger>
                        <SheetContent className="w-[300px] p-0" side="left">
                          <CreateTenderSideNavMenu
                            formGroups={formGroups}
                            activeFormGroup={activeFormGroup}
                            form={form}
                            aiPrefilled={aiImportID ? true : false}
                            inSheet={true}
                            setIsOpen={setIsOpen}
                            formGroupSchemas={formGroupSchemas}
                          />
                        </SheetContent>
                      </Sheet>
                    </div>
                  )
              }
            </div>

            {/* Form */}
            <div
              id="form-scroll-container"
              className="grow h-screen overflow-y-auto pb-[100px]"
            >
              {/* Form Header */}
              <FormGroupHeader
                title={t("newtender.title")}
                description={t("newtender.description")}
              >
                {
                  !aiImportID ? submitButton() : undefined
                }
              </FormGroupHeader>
              <div className="flex gap-2 flex-wrap">
                {isDevMode && (
                  mockDataButton()
                )}
              </div>
              <Form {...form}>
                <form className="space-y-6 pb-[300px]" key={formKey}>
                  {formGroups.map((item, idx) => (
                    <React.Fragment key={item.group}>
                      <Card
                        className={cn("py-4", idx === 0 && "mt-2")}
                        id={item.group}
                        ref={(el) => { formGroupCardRefs.current[idx] = el; }}
                      >
                        {/* Card Header */}
                        <div className="px-4">
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold">{t(`newtender.formgroups.${item.keys[0].toLowerCase()}.title`)}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{item.description}</p>
                          <Separator className="my-2" />
                        </div>

                        {item.components.map((Component, index) => (
                          <div key={index} className="px-4 pt-2">
                            {Component}
                          </div>
                        ))}
                      </Card>
                    </React.Fragment>
                  ))}
                </form>
              </Form>
            </div>
          </div>
        </ResizablePanel>
        {
          aiImportID && (
            <>
              <ResizableHandle withHandle className="ml-2" />
              <ResizablePanel defaultSize={50} minSize={20}>
                <SidebarComponent
                  form={form}
                  documents={aiImportByIdData?.getAIImportById.documents as Document[] ?? []}
                >
                  <div className="flex gap-2">
                    {isDevMode && (
                      mockDataButton()
                    )}
                    {
                      aiImportID ? (
                        submitButton()
                      ) : undefined
                    }
                  </div>
                </SidebarComponent>
              </ResizablePanel>
            </>
          )
        }
      </ResizablePanelGroup>
    </div>
  );
}

export default FormComponent;
